export const MAX_BATCH_SIZE = 50;
export const BATCH_TIMEOUT_MS = 50;

type Resolvers<T> = Set<(value: T | undefined) => void>;
type BatchRequest<K, T> = {
  keys: Set<K>;
  resolversMap: Map<K, Resolvers<T>>;
};

type BatchExecutor<K, T> = (keys: K[]) => Promise<Map<K, T>>;
type BatchExecutorFactory<K, T> = (batchKey: string) => BatchExecutor<K, T>;

/* Generic batch manager that can batch requests for any type of data */
export class BatchManager<K, T> {
  #executingBatches = new Map<string, Set<BatchRequest<K, T>>>();

  #pendingBatches = new Map<string, BatchRequest<K, T>>();

  #timeouts = new Map<string, NodeJS.Timeout>();

  #batchExecutorFactory: BatchExecutorFactory<K, T>;

  #batchTimeoutMs: number;

  constructor(batchExecutorFactory: BatchExecutorFactory<K, T>, batchTimeoutMs: number = BATCH_TIMEOUT_MS) {
    this.#batchExecutorFactory = batchExecutorFactory;
    this.#batchTimeoutMs = batchTimeoutMs;
  }

  async get(batchKey: string, key: K): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve) => {
      if (!key || key === "" || key === null || key === undefined) {
        resolve(undefined);
        return;
      }

      // Check if there is an executing batch for the batch key, and if so, add the resolver to the batch
      const executingBatches = this.#executingBatches.get(batchKey);
      if (executingBatches) {
        for (let i = 0; i < executingBatches.size; i += 1) {
          const batch = Array.from(executingBatches)[i];
          const resolvers = batch.resolversMap.get(key);
          if (resolvers) {
            resolvers.add(resolve);
            return;
          }
        }
      }

      const batch = this.#pendingBatches.get(batchKey) ?? {
        keys: new Set<K>(),
        resolversMap: new Map<K, Resolvers<T>>(),
      };

      batch.keys.add(key);
      const currentResolvers = batch.resolversMap.get(key) ?? new Set();
      currentResolvers.add(resolve);
      batch.resolversMap.set(key, currentResolvers);
      this.#pendingBatches.set(batchKey, batch);

      // Reuse existing timeout if there is one
      if (!this.#timeouts.has(batchKey)) {
        this.#timeouts.set(
          batchKey,
          // oxlint-disable-next-line dart/no-set-timeout
          setTimeout(() => this.#executeBatch(batchKey), this.#batchTimeoutMs)
        );
      }
    });
  }

  async #executeBatch(batchKey: string) {
    const batch = this.#pendingBatches.get(batchKey);
    if (!batch) {
      return;
    }

    const keysList = Array.from(batch.keys);
    const chunks = [];

    // Split keys into chunks
    for (let i = 0; i < keysList.length; i += MAX_BATCH_SIZE) {
      chunks.push(keysList.slice(i, i + MAX_BATCH_SIZE));
    }

    // Remove batch from pending batches
    this.#pendingBatches.delete(batchKey);
    this.#timeouts.delete(batchKey);

    // Add batch to executing batches
    const executingBatch = this.#executingBatches.get(batchKey) ?? new Set();
    executingBatch.add(batch);
    this.#executingBatches.set(batchKey, executingBatch);

    try {
      const batchExecutor = this.#batchExecutorFactory(batchKey);
      const results = await Promise.all(chunks.map((keys) => batchExecutor(keys)));

      const combinedResults = new Map<K, T>();
      results.forEach((result) => {
        result.forEach((value, key) => {
          combinedResults.set(key, value);
        });
      });

      batch.resolversMap.forEach((resolvers, key) => {
        resolvers.forEach((resolve) => resolve(combinedResults.get(key)));
      });
    } catch (error) {
      batch.resolversMap.forEach((resolvers) => {
        resolvers.forEach((resolve) => resolve(undefined));
      });
      throw error;
    } finally {
      executingBatch.delete(batch);
    }
  }
}
